// Catalog.ID Client — Recovery Kit modals (create/reissue + fire drill)
// ============================================================================
// Two ceremonies with two callers each: the Settings pane runs them for the
// SESSION account, the org workspace's Recovery Kit pane runs them for the
// ORGANIZATION under a delegate session. The chrome — heading, reissue warning,
// sheet collector, quorum gate, PASSED/FAILED reporting, close button — is
// identical; only the material source and the verification differ, so those are
// the parameters and everything else lives here once.

import { createElement } from '@catalog-shell';
import type { PaneTypeRenderContext } from '@catalog-shell';
import { createRecoveryKitCreator, type RecoveryKitCreatorConfig } from './recovery-kit-creator';
import { createRecoveryReprint, type RecoveryReprintConfig } from './recovery-kit-reprint';
import { createCardCollector } from './card-scan';
import type { RecoveryCardPayload } from '../crypto/recovery-card';

type Shell = PaneTypeRenderContext['shell'];

const CONTENT_STYLE: Partial<CSSStyleDeclaration> = {
    padding: '20px',
    maxHeight: '80vh',
    overflowY: 'auto',
};

/**
 * Warning shown whenever a kit already exists — a reissue orphans the printed
 * sheets. Round 9 (§16.6) adds the REASON, because the old wording read as a
 * revocation and it is not one: the old set's sheets still combine among
 * themselves, so a reissue only helps once the survivors are physically gone.
 * It also names the cheaper remedy, which now exists.
 */
export const REISSUE_WARNING =
    'Reissuing creates a NEW recovery set. Sheets of the OLD set stop matching the kit on record, but '
    + 'they do not stop working: a quorum of them still reconstructs your supersecret. A reissue is '
    + 'therefore only a remedy once you can collect and destroy every old sheet that is still findable '
    + '— it is what to do when FEWER than the threshold have gone missing, so the lost ones are left '
    + 'alone and useless. If you only need another sheet — a replacement, or one for a new custodian — '
    + 'use "Print sheets…" instead: it prints from the SAME set, so nothing already handed out is '
    + 'disturbed.';

function createContent(heading: string): HTMLElement {
    const content = createElement('div', { className: 'cid-pane-content', style: CONTENT_STYLE });
    content.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: heading }));
    return content;
}

function appendCloseRow(content: HTMLElement, close: () => void): void {
    const closeRow = createElement('div', { className: 'cid-credential-actions', style: { marginTop: '12px' } });
    const closeBtn = createElement('button', { className: 'cs-btn cs-btn--default', textContent: 'Close' });
    closeBtn.addEventListener('click', close);
    closeRow.appendChild(closeBtn);
    content.appendChild(closeRow);
}

// ── Create / reissue ────────────────────────────────────────────────────────

export interface RecoveryKitModalConfig {
    shell: Shell;
    /** True when a kit is already on record — drives the heading and the warning. */
    isReissue: boolean;
    /** Heading override; defaults to "Create/Reissue Recovery Kit". */
    heading?: string;
    /** Everything the creator needs: cid, profile, material source, record hook. */
    creator: RecoveryKitCreatorConfig;
}

export function showRecoveryKitModal(config: RecoveryKitModalConfig): void {
    const content = createContent(
        config.heading ?? (config.isReissue ? 'Reissue Recovery Kit' : 'Create Recovery Kit'),
    );

    if (config.isReissue) {
        content.appendChild(createElement('p', {
            className: 'cid-wizard-warning',
            textContent: REISSUE_WARNING,
        }));
    }

    content.appendChild(createRecoveryKitCreator(config.creator));

    appendCloseRow(content, () => modal.close());

    const modal = config.shell.showModal(content, { size: 'wide' });
}

// ── Reprint (§16.6) ─────────────────────────────────────────────────────────

export interface RecoveryReprintModalConfig {
    shell: Shell;
    /** Heading override; defaults to "Print Recovery Sheets". */
    heading?: string;
    /** Everything the reprint needs: cid, material source, record + writers. */
    reprint: RecoveryReprintConfig;
}

/** Print more sheets of the set the account already holds. Same chrome as the
 *  creator, so the two ceremonies read as one family. */
export function showRecoveryReprintModal(config: RecoveryReprintModalConfig): void {
    const content = createContent(config.heading ?? 'Print Recovery Sheets');
    content.appendChild(createRecoveryReprint(config.reprint));
    appendCloseRow(content, () => modal.close());
    const modal = config.shell.showModal(content, { size: 'wide' });
}

// ── Fire drill (§14) ────────────────────────────────────────────────────────

export interface DrillExtras {
    /** Whether the drill's non-sheet prerequisites are satisfied. */
    isReady(): boolean;
}

export interface RecoveryDrillModalConfig {
    shell: Shell;
    /** Heading override; defaults to "Test Recovery Kit". */
    heading?: string;
    /** Sentence under the heading naming the kit and what the test proves. */
    intro: string;
    /** Extra UI between the sheet collector and the result — the personal drill
     *  adds the encrypted key backup file here. Called once; the callback must
     *  be invoked whenever readiness changes so the run button re-evaluates. */
    extras?: (host: HTMLElement, onReadinessChange: () => void) => DrillExtras;
    /** Run the drill; resolve with the PASSED sentence, throw to fail. */
    run: (cards: RecoveryCardPayload[]) => Promise<string>;
}

/**
 * Fire drill (§14): scanning each sheet is the single-sheet integrity test
 * (14.2 — MAC/set checks happen on decode); the quorum test (14.3)
 * reconstructs the supersecret in memory, verifies it against real
 * ciphertext, and records last-tested on success.
 */
export function showRecoveryDrillModal(config: RecoveryDrillModalConfig): void {
    const content = createContent(config.heading ?? 'Test Recovery Kit');
    content.appendChild(createElement('p', { className: 'cid-pane-note', textContent: config.intro }));

    // §14.2 — every successful scan is a sheet integrity check.
    const collector = createCardCollector();
    content.appendChild(collector.element);

    let extras: DrillExtras | null = null;
    extras = config.extras?.(content, () => refreshRunButton()) ?? null;

    const resultEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(resultEl);

    // §14.3 — quorum recovery test.
    const runBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Run recovery test',
    }) as HTMLButtonElement;
    runBtn.disabled = true;
    function refreshRunButton(): void {
        const cards = collector.getCards();
        runBtn.disabled = (extras ? !extras.isReady() : false)
            || cards.length === 0
            || cards.length < cards[0].thresholdK;
    }
    collector.onChange(refreshRunButton);

    runBtn.addEventListener('click', async () => {
        resultEl.className = 'cid-pane-error';
        resultEl.textContent = '';
        runBtn.setAttribute('disabled', '');
        runBtn.textContent = 'Testing...';
        try {
            const message = await config.run(collector.getCards());
            resultEl.className = 'cid-verify-success';
            resultEl.textContent = message;
        } catch (err) {
            resultEl.className = 'cid-pane-error cid-pane-error--visible';
            resultEl.textContent = `Recovery test FAILED: ${err instanceof Error ? err.message : 'unknown error'}`;
        } finally {
            runBtn.removeAttribute('disabled');
            runBtn.textContent = 'Run recovery test';
        }
    });
    content.appendChild(runBtn);

    appendCloseRow(content, () => modal.close());

    const modal = config.shell.showModal(content, { size: 'wide' });
}
